import {
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
    IconButton, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    TextField, 
    DialogActions, 
    FormControl, 
    InputLabel, 
    Select, 
    Alert, 
  Chip,
    Grid,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { useState, useMemo } from "react";
import { useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';

import {
    useGetEmployeesQuery,
    useGetAllShiftsQuery,
    useGetAllClockingsQuery,
    useCreateClockingMutation,
    useUpdateClockingMutation,
    useDeleteClockingMutation,
    useProcessClockingMutation,
    useGetAllBreaksQuery,

    useCreateBreakMutation,
    useUpdateBreakMutation,
    useDeleteBreakMutation
} from "../../state/api";

const ClockManagement = () => {
    const theme = useTheme();
    
    // Get current user ID from Redux state
    const currentUserId = useSelector((state) => state.global.userId);
    
    const { data: employeesResponse, isLoading: isLoadingEmployees } = useGetEmployeesQuery();
    const { data: shiftsResponse, isLoading: isLoadingShifts } = useGetAllShiftsQuery();
    const { data: clockingsResponse, isLoading: isLoadingClockings } = useGetAllClockingsQuery();
    const { data: breaksResponse } = useGetAllBreaksQuery();
    const [createClocking] = useCreateClockingMutation();
    const [updateClocking] = useUpdateClockingMutation();
    const [deleteClocking] = useDeleteClockingMutation();
    const [processClocking] = useProcessClockingMutation();
    const [createBreak] = useCreateBreakMutation();
    const [updateBreak] = useUpdateBreakMutation();
    const [deleteBreak] = useDeleteBreakMutation();
    
    const employeesData = employeesResponse?.data;
    const shiftsData = Array.isArray(shiftsResponse) ? shiftsResponse : (shiftsResponse?.data || []);
    const allClockings = Array.isArray(clockingsResponse) ? clockingsResponse : (clockingsResponse?.data || []);
    const allBreaks = Array.isArray(breaksResponse) ? breaksResponse : (breaksResponse?.data || []);
    
    const [selectedFilter, setSelectedFilter] = useState("Pending");
    const filterOptions = ["Pending", "Approved", "Rejected", "All"];
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [editableClockingData, setEditableClockingData] = useState(null);
    const [editableBreaks, setEditableBreaks] = useState([]);
    const [validationAlert, setValidationAlert] = useState({ show: false, message: '', severity: 'warning' });
    const [openProcessDialog, setOpenProcessDialog] = useState(false);
    const [processAction, setProcessAction] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    
    const [newClockingData, setNewClockingData] = useState({
        employeeId: "",
        shiftId: "",
        clockInTime: moment().format("YYYY-MM-DDTHH:mm"),
        clockOutTime: "",
        isAbsent: false,
        notes: ""
    });

    const handleMenuClick = (event, params) => {
        setAnchorEl(event.currentTarget);
        setSelectedRow(params.row);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleViewOpen = () => {
        setOpenViewDialog(true);
        handleMenuClose();
    };

    const handleEditOpen = () => {
        if (selectedRow) {
            // Extract the actual employeeId - handle both object and string cases
            let employeeId = selectedRow.employeeId;
            if (typeof selectedRow.employeeId === 'object' && selectedRow.employeeId._id) {
                employeeId = selectedRow.employeeId._id;
            }

            setEditableClockingData({
                ...selectedRow,
                employeeId: employeeId, // Use the extracted ID
                clockInTime: selectedRow.clockInTime ? moment(selectedRow.clockInTime).format("YYYY-MM-DDTHH:mm") : "",
                clockOutTime: selectedRow.clockOutTime ? moment(selectedRow.clockOutTime).format("YYYY-MM-DDTHH:mm") : "",
            });
            
            // Load breaks for this clocking record
            const clockingBreaks = allBreaks.filter(breakRecord => breakRecord.clockingId === selectedRow._id);
            setEditableBreaks(clockingBreaks.map(breakRecord => ({
                ...breakRecord,
                breakStartTime: breakRecord.breakStartTime ? moment(breakRecord.breakStartTime).format("YYYY-MM-DDTHH:mm") : "",
                breakEndTime: breakRecord.breakEndTime ? moment(breakRecord.breakEndTime).format("YYYY-MM-DDTHH:mm") : "",
            })));
            
            setOpenEditDialog(true);
        }
        handleMenuClose();
    };

    const handleAddDialogOpen = () => {
        setOpenAddDialog(true);
        setValidationAlert({ show: false, message: '', severity: 'warning' });
    };
    
    const handleAddDialogClose = () => {
        setOpenAddDialog(false);
        setValidationAlert({ show: false, message: '', severity: 'warning' });
        setNewClockingData({
            employeeId: "",
            shiftId: "",
            clockInTime: moment().format("YYYY-MM-DDTHH:mm"),
            clockOutTime: "",
            isAbsent: false,
            notes: ""
        });
    };

    const handleNewClockingInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewClockingData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };
    
    const handleAddSubmit = async () => {
        if (!newClockingData.employeeId || !newClockingData.shiftId) {
            setValidationAlert({ show: true, message: "Please select an employee and shift.", severity: 'error' });
            return;
        }

        try {
            const clockingToCreate = {
                ...newClockingData,
                clockInTime: newClockingData.clockInTime ? new Date(newClockingData.clockInTime).toISOString() : null,
                clockOutTime: newClockingData.clockOutTime ? new Date(newClockingData.clockOutTime).toISOString() : null,
                userId: currentUserId,
            };

            await createClocking(clockingToCreate).unwrap();
            handleAddDialogClose();
        } catch (error) {
    
            setValidationAlert({ show: true, message: `Failed to create clocking: ${error.data?.message || error.message}`, severity: 'error' });
        }
    };

    const handleEditSubmit = async () => {
        if (!editableClockingData) return;

        try {
            const clockingToUpdate = {
                ...editableClockingData,
                clockInTime: editableClockingData.clockInTime ? new Date(editableClockingData.clockInTime).toISOString() : null,
                clockOutTime: editableClockingData.clockOutTime ? new Date(editableClockingData.clockOutTime).toISOString() : null,
                userId: currentUserId,
            };

            await updateClocking({ id: editableClockingData._id, ...clockingToUpdate }).unwrap();
            
            // Update breaks
            for (const breakRecord of editableBreaks) {
                const breakToUpdate = {
                    ...breakRecord,
                    breakStartTime: breakRecord.breakStartTime ? new Date(breakRecord.breakStartTime).toISOString() : null,
                    breakEndTime: breakRecord.breakEndTime ? new Date(breakRecord.breakEndTime).toISOString() : null,
                    userId: currentUserId,
                };

                if (breakRecord._id && breakRecord._id.startsWith('new_')) {
                    // Create new break
                    const { _id, ...newBreakData } = breakToUpdate;
                    newBreakData.clockingId = editableClockingData._id;
                    newBreakData.employeeId = editableClockingData.employeeId;
                    await createBreak(newBreakData).unwrap();
                } else if (breakRecord._id) {
                    // Update existing break
                    await updateBreak({ id: breakRecord._id, ...breakToUpdate }).unwrap();
                }
            }
            
            setOpenEditDialog(false);
            setEditableClockingData(null);
            setEditableBreaks([]);
        } catch (error) {
    
            setValidationAlert({ show: true, message: `Failed to update clocking: ${error.data?.message || error.message}`, severity: 'error' });
        }
    };

    const handleDeleteOpen = () => {
        setOpenDeleteDialog(true);
        handleMenuClose();
    };

    const handleDelete = async () => {
        if (selectedRow) {
            try {
                await deleteClocking({ 
                    id: selectedRow._id, 
                    userId: currentUserId
                });
                setOpenDeleteDialog(false);
            } catch (error) {
        
            }
        }
    };

    const handleProcessOpen = (action) => {
        setProcessAction(action);
        setOpenProcessDialog(true);
        setRejectionReason('');
        handleMenuClose();
    };

    const handleProcessSubmit = async () => {
        if (!selectedRow) return;

        if (processAction === 'Rejected' && !rejectionReason.trim()) {
            setValidationAlert({ show: true, message: "Rejection reason is required.", severity: 'error' });
            return;
        }

        try {
            await processClocking({
                id: selectedRow._id,
                approvalStatus: processAction,
                approvedBy: currentUserId,
                rejectionReason: processAction === 'Rejected' ? rejectionReason : undefined,
                userId: currentUserId,
            }).unwrap();
            setOpenProcessDialog(false);
        } catch (error) {
    
            setValidationAlert({ show: true, message: `Failed to ${processAction.toLowerCase()} clocking: ${error.data?.message || error.message}`, severity: 'error' });
        }
    };

    const processedData = useMemo(() => {
        if (!allClockings || !employeesData || isLoadingClockings || isLoadingEmployees) {
            return [];
        }

        let dataToProcess = Array.isArray(allClockings) ? allClockings : [];

        if (dataToProcess.length === 0) return [];

        let filteredData = dataToProcess;
        
        // Apply status filter
        if (selectedFilter !== "All") {
            filteredData = dataToProcess.filter(clocking => clocking.approvalStatus === selectedFilter);
        }

        return filteredData.map(clocking => {
            let employeeName = 'Unknown Employee';
            
            // Check if employeeId is populated (is an object) or just an ID string
            if (clocking.employeeId && typeof clocking.employeeId === 'object') {
                const { firstName, lastName } = clocking.employeeId.names;
                employeeName = `${firstName} ${lastName}`;
            } else {
                // Fallback for non-populated ID
                const employee = employeesData.find(emp => emp._id === clocking.employeeId);
                if (employee) {
                    employeeName = `${employee.names.firstName} ${employee.names.lastName}`;
                }
            }
            
            // Calculate hours worked and break time display
            const hoursWorked = clocking.totalHoursWorked ? `${clocking.totalHoursWorked.toFixed(2)}h` : 'N/A';
            const breakTime = clocking.totalBreakTime ? `${(clocking.totalBreakTime / 60).toFixed(2)}h` : 'N/A';
            
            // Get breaks for this clocking record
            const clockingBreaks = allBreaks.filter(breakRecord => breakRecord.clockingId === clocking._id);
            const breaksDisplay = clockingBreaks.length > 0 
                ? `${clockingBreaks.length} break${clockingBreaks.length > 1 ? 's' : ''}`
                : 'No breaks';
            
            return {
                ...clocking,
                id: clocking._id,
                employeeName,
                displayClockIn: clocking.clockInTime ? moment(clocking.clockInTime).format("DD/MM/YYYY HH:mm") : 'N/A',
                displayClockOut: clocking.clockOutTime ? moment(clocking.clockOutTime).format("DD/MM/YYYY HH:mm") : 'Not clocked out',
                displayDate: clocking.clockInTime ? moment(clocking.clockInTime).format("DD/MM/YYYY") : 'N/A',
                hoursWorked,
                breakTime,
                breaksDisplay,
                breaks: clockingBreaks,
            };
        });
    }, [clockingsResponse, employeesData, breaksResponse, isLoadingClockings, isLoadingEmployees, selectedFilter]);

    const addNewBreak = () => {
        const newBreak = {
            _id: `new_${Date.now()}`,
            breakStartTime: "",
            breakEndTime: "",
            status: "ongoing"
        };
        setEditableBreaks([...editableBreaks, newBreak]);
    };

    const removeBreak = async (breakIndex, breakId) => {
        if (breakId && !breakId.startsWith('new_')) {
            try {
                await deleteBreak({ id: breakId, userId: currentUserId }).unwrap();
            } catch (error) {
        
            }
        }
        setEditableBreaks(editableBreaks.filter((_, index) => index !== breakIndex));
    };

    const updateBreakField = (breakIndex, field, value) => {
        const updatedBreaks = [...editableBreaks];
        updatedBreaks[breakIndex] = { ...updatedBreaks[breakIndex], [field]: value };
        setEditableBreaks(updatedBreaks);
    };

    const columns = [
        {
            field: "employeeName",
            headerName: "Employee",
            flex: 1,
            minWidth: 150,
        },
        {
            field: "displayDate",
            headerName: "Date",
            flex: 0.8,
            minWidth: 100,
        },
        {
            field: "displayClockIn",
            headerName: "Clock In",
            flex: 1,
            minWidth: 120,
        },
        {
            field: "displayClockOut",
            headerName: "Clock Out",
            flex: 1,
            minWidth: 120,
        },
        {
            field: "hoursWorked",
            headerName: "Hours Worked",
            flex: 0.8,
            minWidth: 100,
        },
        {
            field: "breakTime",
            headerName: "Break Time",
            flex: 0.8,
            minWidth: 100,
        },
        {
            field: "breaksDisplay",
            headerName: "Breaks",
            flex: 0.8,
            minWidth: 100,
        },
        {
            field: "isAbsent",
            headerName: "Status",
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value ? "Absent" : "Present"}
                    color={params.value ? "error" : "success"}
                    variant="outlined"
                    size="small"
                />
            ),
        },
        {
            field: "approvalStatus",
            headerName: "Approval",
            flex: 0.8,
            minWidth: 100,
            renderCell: (params) => {
                const getStatusColor = (status) => {
                    switch (status) {
                        case 'Approved':
                            return 'success';
                        case 'Rejected':
                            return 'error';
                        case 'Pending':
                        default:
                            return 'warning';
                    }
                };

                return (
                    <Chip
                        label={params.value}
                        color={getStatusColor(params.value)}
                        variant="outlined"
                        size="small"
                    />
                );
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <IconButton
                    onClick={(event) => handleMenuClick(event, params)}
                    size="small"
                >
                    <MoreVertIcon />
                </IconButton>
            ),
        },
    ];

    if (isLoadingClockings || isLoadingEmployees || isLoadingShifts) {
        return <Typography>Loading...</Typography>;
    }

    return (
        <Box m="1.5rem 2.5rem">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="1rem">
                <Typography variant="h4" color={theme.palette.secondary[100]}>
                    Clock Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddDialogOpen}
                    sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: theme.palette.background.alt,
                        fontSize: "14px",
                        fontWeight: "bold",
                        padding: "10px 20px",
                    }}
                >
                    Add Clock Record
                </Button>
            </Box>

            {/* Filter Buttons */}
            <Box display="flex" gap="10px" mb="1rem">
                {filterOptions.map((option) => (
                    <Button
                        key={option}
                        variant={selectedFilter === option ? "contained" : "outlined"}
                        onClick={() => setSelectedFilter(option)}
                        sx={{
                            backgroundColor: selectedFilter === option ? theme.palette.secondary.light : "transparent",
                            color: selectedFilter === option ? theme.palette.background.alt : theme.palette.secondary[100],
                            borderColor: theme.palette.secondary[100],
                        }}
                    >
                        {option}
                    </Button>
                ))}
            </Box>

            <Box
                height="75vh"
                sx={{
                    "& .MuiDataGrid-root": { border: "none", backgroundColor: "transparent" },
                    "& .MuiDataGrid-row": {
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        borderRadius: "1px", cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: theme.palette.secondary.light
                        }
                    },
                    "& .MuiDataGrid-cell": { borderBottom: "none" },
                    "& .name-column--cell": { color: theme.palette.secondary[200], fontWeight: "bold" },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: theme.palette.primary.light,
                        borderBottom: `1px solid ${theme.palette.secondary[300]}`,
                        color: theme.palette.secondary[100],
                    },
                    "& .MuiDataGrid-virtualScroller": { backgroundColor: theme.palette.background.alt },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: `1px solid ${theme.palette.secondary[300]}`,
                        backgroundColor: theme.palette.primary.light,
                        color: theme.palette.secondary[100],
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${theme.palette.secondary[200]} !important`, fontSize: "16px" },
                }}
            >
                <DataGrid
                    loading={isLoadingClockings || isLoadingEmployees}
                    getRowId={(row) => row._id}
                    rows={processedData}
                    columns={columns}
                />
            </Box>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={handleViewOpen}>
                    <VisibilityIcon sx={{ mr: 1 }} />
                    View Details
                </MenuItem>
                <MenuItem onClick={handleEditOpen}>
                    <EditIcon sx={{ mr: 1 }} />
                    Edit
                </MenuItem>
                {selectedRow?.approvalStatus === 'Pending' && (
                    [
                        <MenuItem key="approve" onClick={() => handleProcessOpen('Approved')} sx={{ color: 'success.main' }}>
                            <ThumbUpIcon sx={{ mr: 1 }} />
                            Approve
                        </MenuItem>,
                        <MenuItem key="reject" onClick={() => handleProcessOpen('Rejected')} sx={{ color: 'error.main' }}>
                            <ThumbDownIcon sx={{ mr: 1 }} />
                            Reject
                        </MenuItem>
                    ]
                )}
                <MenuItem onClick={handleDeleteOpen} sx={{ color: 'error.main' }}>
                    <DeleteIcon sx={{ mr: 1 }} />
                    Delete
                </MenuItem>
            </Menu>

            {/* View Dialog */}
            <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Clock Record Details</DialogTitle>
                <DialogContent>
                    {selectedRow && (
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="h6" gutterBottom>
                                        {selectedRow.employeeName}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        Date: {selectedRow.displayDate}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Clock In: {selectedRow.displayClockIn}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Clock Out: {selectedRow.displayClockOut}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" gutterBottom>
                                        Hours Worked: {selectedRow.hoursWorked}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Total Break Time: {selectedRow.breakTime}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Status: <Chip 
                                            label={selectedRow.isAbsent ? "Absent" : "Present"} 
                                            color={selectedRow.isAbsent ? "error" : "success"}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Approval: <Chip 
                                            label={selectedRow.approvalStatus} 
                                            color={selectedRow.approvalStatus === 'Approved' ? 'success' : selectedRow.approvalStatus === 'Rejected' ? 'error' : 'warning'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Typography>
                                </Grid>
                                {selectedRow.notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" gutterBottom>
                                            <strong>Notes:</strong> {selectedRow.notes}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                            
                            {/* Breaks Section */}
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Break Records ({selectedRow.breaks?.length || 0})
                                </Typography>
                                
                                {selectedRow.breaks && selectedRow.breaks.length > 0 ? (
                                    selectedRow.breaks.map((breakRecord, index) => {
                                        // Calculate duration
                                        let duration = 'N/A';
                                        if (breakRecord.breakStartTime && breakRecord.breakEndTime) {
                                            const start = new Date(breakRecord.breakStartTime);
                                            const end = new Date(breakRecord.breakEndTime);
                                            const durationMinutes = (end - start) / (1000 * 60);
                                            duration = `${Math.floor(durationMinutes / 60)}h ${Math.round(durationMinutes % 60)}m`;
                                        } else if (breakRecord.duration) {
                                            const totalMinutes = breakRecord.duration;
                                            duration = `${Math.floor(totalMinutes / 60)}h ${Math.round(totalMinutes % 60)}m`;
                                        }
                                        
                                        return (
                                            <Box key={breakRecord._id || index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={3}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Start Time
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {breakRecord.breakStartTime 
                                                                ? moment(breakRecord.breakStartTime).format("DD/MM/YYYY HH:mm")
                                                                : 'Not set'
                                                            }
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            End Time
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {breakRecord.breakEndTime 
                                                                ? moment(breakRecord.breakEndTime).format("DD/MM/YYYY HH:mm")
                                                                : breakRecord.status === 'ongoing' ? 'Ongoing' : 'Not set'
                                                            }
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Duration
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            {duration}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Status
                                                        </Typography>
                                                        <Chip 
                                                            label={breakRecord.status || 'ongoing'}
                                                            color={
                                                                breakRecord.status === 'completed' ? 'success' : 
                                                                breakRecord.status === 'cancelled' ? 'error' : 
                                                                'warning'
                                                            }
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                                        No breaks recorded for this clocking session.
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenViewDialog(false)}
                        variant="contained"
                        color="secondary"
                        sx={{
                            backgroundColor: theme.palette.secondary.main,
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Dialog */}
            <Dialog open={openAddDialog} onClose={handleAddDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>Add New Clock Record</DialogTitle>
                <DialogContent>
                    {validationAlert.show && (
                        <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
                            {validationAlert.message}
                        </Alert>
                    )}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Employee</InputLabel>
                                <Select
                                    name="employeeId"
                                    value={newClockingData.employeeId}
                                    onChange={handleNewClockingInputChange}
                                    label="Employee"
                                >
                                    {employeesData?.map((employee) => (
                                        <MenuItem key={employee._id} value={employee._id}>
                                            {employee.names.firstName} {employee.names.lastName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Shift</InputLabel>
                                <Select
                                    name="shiftId"
                                    value={newClockingData.shiftId}
                                    onChange={handleNewClockingInputChange}
                                    label="Shift"
                                >
                                    {shiftsData?.map((shift) => (
                                        <MenuItem key={shift._id} value={shift._id}>
                                            {shift.name} - {moment(shift.date).format("DD/MM/YYYY")}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="clockInTime"
                                label="Clock In Time"
                                type="datetime-local"
                                value={newClockingData.clockInTime}
                                onChange={handleNewClockingInputChange}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="clockOutTime"
                                label="Clock Out Time"
                                type="datetime-local"
                                value={newClockingData.clockOutTime}
                                onChange={handleNewClockingInputChange}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={newClockingData.isAbsent}
                                        onChange={handleNewClockingInputChange}
                                        name="isAbsent"
                                    />
                                }
                                label="Mark as Absent"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="notes"
                                label="Notes"
                                value={newClockingData.notes}
                                onChange={handleNewClockingInputChange}
                                fullWidth
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAddDialogClose}>Cancel</Button>
                    <Button onClick={handleAddSubmit} variant="contained">
                        Add Clock Record
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Edit Clocking Record</DialogTitle>
                <DialogContent>
                    {validationAlert.show && (
                        <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
                            {validationAlert.message}
                        </Alert>
                    )}
                    {editableClockingData && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                             <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Employee</InputLabel>
                                    <Select
                                        name="employeeId"
                                        value={editableClockingData.employeeId}
                                        onChange={(e) => setEditableClockingData(prev => ({ ...prev, employeeId: e.target.value }))}
                                        label="Employee"
                                        disabled
                                    >
                                        {employeesData?.map((employee) => (
                                            <MenuItem key={employee._id} value={employee._id}>
                                                {employee.names.firstName} {employee.names.lastName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Shift</InputLabel>
                                    <Select
                                        name="shiftId"
                                        value={editableClockingData.shiftId}
                                        onChange={(e) => setEditableClockingData(prev => ({ ...prev, shiftId: e.target.value }))}
                                        label="Shift"
                                    >
                                        {shiftsData?.map((shift) => (
                                            <MenuItem key={shift._id} value={shift._id}>
                                                {`${shift.name} (${moment(shift.startTime).format('HH:mm')} - ${moment(shift.endTime).format('HH:mm')})`}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="clockInTime"
                                    label="Clock-In Time"
                                    type="datetime-local"
                                    value={editableClockingData.clockInTime}
                                    onChange={(e) => setEditableClockingData(prev => ({ ...prev, clockInTime: e.target.value }))}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    name="clockOutTime"
                                    label="Clock-Out Time"
                                    type="datetime-local"
                                    value={editableClockingData.clockOutTime}
                                    onChange={(e) => setEditableClockingData(prev => ({ ...prev, clockOutTime: e.target.value }))}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={editableClockingData.isAbsent}
                                            onChange={(e) => setEditableClockingData(prev => ({ ...prev, isAbsent: e.target.checked }))}
                                            name="isAbsent"
                                        />
                                    }
                                    label="Mark as Absent"
                                />
                            </Grid>
                             <Grid item xs={12}>
                                <TextField
                                    name="notes"
                                    label="Notes"
                                    multiline
                                    rows={3}
                                    value={editableClockingData.notes}
                                    onChange={(e) => setEditableClockingData(prev => ({ ...prev, notes: e.target.value }))}
                                    fullWidth
                                />
                            </Grid>
                            
                            {/* Breaks Section */}
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">Breaks</Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={addNewBreak}
                                        startIcon={<AddIcon />}
                                        size="small"
                                    >
                                        Add Break
                                    </Button>
                                </Box>
                                
                                {editableBreaks.map((breakRecord, index) => (
                                    <Box key={breakRecord._id || index} sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="Break Start"
                                                    type="datetime-local"
                                                    value={breakRecord.breakStartTime}
                                                    onChange={(e) => updateBreakField(index, 'breakStartTime', e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="Break End"
                                                    type="datetime-local"
                                                    value={breakRecord.breakEndTime}
                                                    onChange={(e) => updateBreakField(index, 'breakEndTime', e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Status</InputLabel>
                                                    <Select
                                                        value={breakRecord.status}
                                                        onChange={(e) => updateBreakField(index, 'status', e.target.value)}
                                                        label="Status"
                                                    >
                                                        <MenuItem value="ongoing">Ongoing</MenuItem>
                                                        <MenuItem value="completed">Completed</MenuItem>
                                                        <MenuItem value="cancelled">Cancelled</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={1}>
                                                <IconButton
                                                    onClick={() => removeBreak(index, breakRecord._id)}
                                                    color="error"
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}
                                
                                {editableBreaks.length === 0 && (
                                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                                        No breaks recorded. Click "Add Break" to add one.
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setOpenEditDialog(false)}
                        variant="contained"
                        sx={{ backgroundColor: theme.palette.error.main }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        variant="contained"
                        sx={{ backgroundColor: theme.palette.secondary.main }}
                    >
                        Update Clocking
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Process Dialog */}
            <Dialog open={openProcessDialog} onClose={() => setOpenProcessDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{processAction} Clocking Record</DialogTitle>
                <DialogContent>
                    {validationAlert.show && (
                        <Alert severity={validationAlert.severity} sx={{ mb: 2 }}>
                            {validationAlert.message}
                        </Alert>
                    )}
                    <Typography>
                        Are you sure you want to <strong>{processAction?.toLowerCase()}</strong> this clocking record for {selectedRow?.employeeName}?
                    </Typography>
                    {processAction === 'Rejected' && (
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Rejection Reason"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            multiline
                            rows={3}
                            sx={{ mt: 2 }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenProcessDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleProcessSubmit}
                        variant="contained"
                        color={processAction === 'Approved' ? 'success' : 'error'}
                    >
                        Confirm {processAction}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                <DialogTitle>Delete Clock Record</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the clock record for {selectedRow?.employeeName}?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClockManagement;
